/**
 * Feature flags de V2.
 *
 * Sirven para dejar módulos en desarrollo dentro del código sin exponerlos a los
 * usuarios. El flag controla **visibilidad** (entrada de menú + ruta), que es
 * distinto de la fuente de datos.
 */

/**
 * Módulo Track Shipment (tracking aéreo/marítimo).
 *
 * OFF por defecto: si la variable no está definida, el módulo NO se muestra.
 * Así queda invisible en producción hasta que se decida publicarlo, aunque el
 * código ya esté desplegado.
 *
 * ⚠️ No confundir con `REACT_APP_TRACKING_LIVE`, que solo decide si se consulta
 * al proveedor real o se usan datos de ejemplo. Para publicarlo en producción
 * hacen falta LAS DOS:
 *   REACT_APP_FEATURE_TRACKING=true   → se muestra el módulo
 *   REACT_APP_TRACKING_LIVE=true      → consulta datos reales (si no, muestra demo)
 */
export const FEATURE_TRACKING = process.env.REACT_APP_FEATURE_TRACKING === 'true';

/**
 * Módulos multimodales: Ocean, Air, Global View y Global Dashboard.
 *
 * OFF por defecto, igual que el anterior: permite desplegar el código en
 * producción sin exponer pantallas a medias. Lo terrestre (`/loads`) NO depende
 * de este flag y sigue funcionando siempre.
 *
 * 🔄 28-oct-2026: la **pre-alerta** tampoco (`/pre-alert` y `/pre-alert/:token`): se publica antes que aéreo y
 * marítimo, y la abren sus permisos (`PA_VIEW`).
 *
 *   REACT_APP_FEATURE_MULTIMODAL=true
 */
export const FEATURE_MULTIMODAL =
  process.env.REACT_APP_FEATURE_MULTIMODAL === 'true';

/**
 * Los avisos por correo a los clientes: la tarjeta de contactos del detalle de un cliente.
 *
 * 🔄 15-oct-2026: OFF para todos hasta que el usuario lo diga: *"hay que ocultar por ahora la configuración de email,
 * todavía no les daremos esa opción hasta que el equipo me confirme que funcionan bien las notificaciones … (yo te diré
 * cuando lo activamos, por el momento lo ocultamos para todos)"*.
 *
 * 📌 Ocultarlo no apaga nada en el servidor, y no hace falta: a un contacto solo le llega correo si aceptó la
 * invitación, y al 15-oct el único capturado nunca se invitó.
 *
 *   REACT_APP_FEATURE_CLIENT_EMAILS=true
 */
export const FEATURE_CLIENT_EMAILS = process.env.REACT_APP_FEATURE_CLIENT_EMAILS === 'true';

/**
 * Los avisos de UN embarque desde su detalle: *Notify me* (seguirlo y elegir de qué avisar) y *Send tracking* (mandarle
 * su estatus a quien lo sigue). En terrestre, marítimo y aéreo.
 *
 * 🔄 19-oct-2026: OFF para todos hasta que el usuario lo diga: *"dejaremos estos apartados para después, tanto la
 * configuración de alerta y de alertar a los demás, es probable que ocupemos un permiso nuevo para notificar a los demás
 * con el botón"*. Hasta aquí el botón de *Send tracking* le salía a todos y el servidor pide `SH_VIEW`: a 6 de los 22
 * activos les respondía 403.
 *
 * 📌 Ocultarlos no apaga nada en el servidor, como con los correos a clientes: las preferencias de *My profile* y la
 * campanita siguen igual, y al 19-oct la única suscripción a un embarque es de Dylo System.
 *
 *   REACT_APP_FEATURE_SHIPMENT_ALERTS=true
 */
export const FEATURE_SHIPMENT_ALERTS = process.env.REACT_APP_FEATURE_SHIPMENT_ALERTS === 'true';

/**
 * Carga diferida de las pantallas pesadas (`React.lazy`).
 *
 * ## Qué cambia
 *
 * Sin esto, el bundle principal incluye **todas** las pantallas, así que abrir el login
 * descarga también el detalle de carga, el mapa de Mapbox y el generador de pre-alerta. Con
 * la carga diferida, cada pantalla llega en su propio archivo la primera vez que se visita.
 *
 * ## 🔑 Por qué el interruptor es al revés de los otros flags
 *
 * Los demás están **apagados** por omisión porque esconden módulos a medias. Este está
 * **encendido**, porque no es una función nueva que se pueda filtrar sin querer: es cómo se
 * entrega el mismo código. Lo que se necesitaba era **un retorno fácil**, y eso es lo que
 * hace la variable:
 *
 *   REACT_APP_EAGER_ROUTES=true   → vuelve exactamente al comportamiento anterior
 *
 * Así se puede comparar lado a lado en local, y si en producción apareciera algo raro se
 * revierte cambiando una variable en el panel, sin tocar código ni rehacer el despliegue
 * desde cero.
 *
 * 📌 La falla de la carga diferida **se nota de inmediato** —una pantalla en blanco o un
 * parpadeo al navegar—, no en silencio. Por eso es razonable dejarla encendida por omisión:
 * si algo se rompe, se ve al primer clic.
 */
export const LAZY_ROUTES = process.env.REACT_APP_EAGER_ROUTES !== 'true';
